<template>
    <div :key="rowId" class="table-cell-wrapper_content / flex">
        <VJoyTooltip v-if="showAcceptanceBlockedTooltip" variant="secondary" placement="bottom">
            <div ref="cellContent">
                <TableStatusBadge :status="status" />
            </div>
            <template #tooltip-content>
                <p>
                    <span class="table-status__tooltip__title">
                        {{ t('projectclient.projectlist.table.status.tooltip.acceptanceBlocked.title') }}
                    </span>
                    <VJoyText preset="primary-200">
                        {{ t('projectclient.projectlist.table.status.tooltip.acceptanceBlocked.content') }}
                    </VJoyText>
                </p>
            </template>
        </VJoyTooltip>
        <TableStatusBadge v-else :status="status" />

        <VJoyTooltip v-if="quoteExpirationIsComing" variant="secondary" class="joy-tooltip table-status__tooltip" placement="bottom">
            <div ref="cellContent">
                <VJoyIcon name="warning-triangle" color="warning" />
            </div>
            <template #tooltip-content>
                <p>
                    This quote will expire on
                    <span class="joy-font-primary-250">{{ formatDateToShort(expirationDate) }}.</span>
                </p>
            </template>
        </VJoyTooltip>
    </div>
</template>

<script lang="ts" setup>
    import {computed} from 'vue';
    import {useUserStore} from '@common/stores';
    import {isWithinInterval, subDays} from 'date-fns';
    import {useFormat, useTranslation} from '#imports';
    import {VJoyIcon, VJoyText, VJoyTooltip} from '@maltjoy/core-vue';
    import {ProjectStatus} from '@api';
    import TableStatusBadge from './TableStatusBadge.vue';

    const {t} = useTranslation();

    const props = defineProps<{
        rowId: string;
        status: ProjectStatus;
        expirationDate?: Date | null;
    }>();

    const showAcceptanceBlockedTooltip = computed(() => props.status === ProjectStatus.AcceptanceBlocked);

    const {formatDateToShort} = useFormat();
    const {isPlatformAdmin} = useUserStore();
    const quoteExpirationIsComing = computed(
        () => isPlatformAdmin && props.status === ProjectStatus.ToApprove && props.expirationDate && isWithinExpirationWindow(props.expirationDate),
    );

    function isWithinExpirationWindow(expirationDate: Date): boolean {
        const today = new Date();
        const sevenDaysBefore = subDays(expirationDate, 7);

        return isWithinInterval(today, {
            start: sevenDaysBefore,
            end: expirationDate,
        });
    }
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .table-cell-wrapper_content {
        flex-flow: row nowrap;
    }

    .table-status__tooltip {
        margin-left: var(--joy-core-spacing-2);
        margin-top: 3%;
        display: inline-block;

        &__title {
            @include text.getFontBySize(primary-250);

            padding-bottom: var(--joy-core-spacing-2);
        }
    }
</style>
